import {html, css} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  fromEvent,
  of,
  map,
  Observable,
  startWith,
  shareReplay,
  distinctUntilChanged,
} from 'rxjs';
import tippy, {Instance as TippyInstance} from 'tippy.js';
import Decimal, {DecimalSource} from './break_infinity.js';
import {observe} from './directives/observe.js';
import global_css from './global-css.js';
import {ri} from './runner-idle.js';
import {StatName, StatSaveData} from './types/runner-idle.js';
import * as D from './decimal_utils';

import {fancy, formatStat, formatStatDelta} from './utils.js';
import {ValuefulUnlockableLitElement} from './valueful-unlockable-lit-element.js';
import {tap} from 'ramda';

@customElement('ri-stat')
export class Stat extends ValuefulUnlockableLitElement {
  // #region Properties (14)
  public static create(overrides: Partial<Stat>) {
    const instance = document.createElement('ri-stat');
    instance.setup(overrides);
    instance.baseValue$.next(new Decimal(overrides.value ?? instance.value));
    return instance;
  }

  public static override styles = [
    global_css,
    css`
      :host {
        min-width: 7ch;
        height: 6em;
      }
      .container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        padding-bottom: 4px;
      }
      .container * {
        min-height: 1fr;
      }
      .value {
        font-size: 1rem;
      }
      input.value-control {
        font-size: 1rem;
        width: 5ch;
        color: black;
      }
    `,
  ];

  @property() public description = 'Description';
  @property() public override displayName = 'Stat';
  @property() public override name: StatName = 'strength';
  @property() public shortName = '???';
  @state() override baseValue$ = new BehaviorSubject(new Decimal(0));
  @state() public delta = new Decimal(0);

  public override baseTags = new Set(['stat']);
  public override initialValue: Decimal = new Decimal(0);
  public tooltip: TippyInstance;
  valueControlInput?: HTMLInputElement;
  valueControlInputValue$!: Observable<Decimal | null>;

  public get fancyShortName() {
    return fancy(this.name, this.shortName);
  }

  public get valueHTML() {
    return this.getValueHTML();
  }

  override loadFromJSON(data: StatSaveData) {
    this.baseValue$.next(new Decimal(data.maxValue as Decimal));
    this.baseValue = new Decimal(data.baseValue);
    this.unlocked = data.unlocked;
    this.secret = data.secret;
    this.new = data.new;
    this.hiddenByPlayer = data.hiddenByPlayer;
  }

  // #endregion Properties (14)

  // #region Constructors (1)

  constructor() {
    super();
    this.tooltip = tippy(this, {
      content: `${this.description}`,
      placement: 'top-start',
      allowHTML: true,
    });
    this.valueControlInput = document.createElement('input');
    this.valueControlInput.classList.add('value-control');
    this.valueControlInputValue$ = fromEvent(
      this.valueControlInput!,
      'input'
    ).pipe(
      map((event) => {
        let value: Decimal | null = null;
        try {
          value = new Decimal((event?.target as HTMLInputElement)?.value);
        } catch (e) {
          console.error(e);
        }
        return value;
      }),
      startWith(null),
      shareReplay(1)
    );
    this.value$ = combineLatest([
      this.baseValue$,
      this.valueControlInputValue$,
      ri.isDev$,
    ]).pipe(
      filter((args) => args.every((x) => x !== undefined)),
      map(([baseValue, controlValue, isDev]) => {
        if (controlValue && (isDev || baseValue.gt(controlValue)))
          return controlValue;
        return baseValue;
      })
    );
  }

  // #endregion Constructors (1)

  // #region Public Methods (6)

  public getValueHTML(fancy = true, named = true) {
    return `<span>
              <span class='${this.name} ${fancy ? 'fancy' : ''} stat'>
                ${formatStat(this.value)}
              </span>
              ${named ? this.shortName : ''}
            </span>`;
  }

  public initializeValue(value: DecimalSource) {
    this.setValue(value);
    this.setInitials();
  }

  public override render() {
    this.tooltip.setContent(
      `<strong>${this.displayName}</strong><br>${this.description} `
    );
    return html`
      <div class=${this.name + ' fancy container'}>
        <div class="value">${unsafeHTML(this.getValueHTML(false, false))}</div>
        <label> ${this.valueControlInput} </label>
        <div class="short-name">${this.shortName}</div>
        <div class="delta">${formatStatDelta(this.delta)}</div>
      </div>
    `;
  }

  public override resetToInitials() {
    super.resetToInitials();
    this.setValue(this.initialValue);
  }

  // public override setInitials() {
  //   this.initialValue = this.value;
  // }

  // #endregion Public Methods (6)
}
